import { motion } from 'framer-motion';
import {
  Server,
  FileText,
  MessageSquare,
  Zap,
  Shield,
  DollarSign,
  Settings,
  ArrowRight
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useCurrentLang } from '../lib/hooks/useCurrentLang';
import { getDelay, getDuration, getWillChangeStyle } from '../lib/animations/optimized-animations';

const featureKeys = [
  { icon: Server, key: 'localHosting' },
  { icon: Zap, key: 'aiRag' },
  { icon: FileText, key: 'documentAutomation' },
  { icon: MessageSquare, key: 'intelligentChat' },
  { icon: Settings, key: 'seamlessIntegration' },
  { icon: Shield, key: 'security' },
  { icon: DollarSign, key: 'affordableHardware' },
  { icon: Settings, key: 'fullCustomization' }
];

export function BunkerSection() {
  const { t } = useTranslation('home');
  const currentLang = useCurrentLang();
  return (
    <section id="bunker" className="py-20 bg-gradient-to-br from-gray-50 to-blue-50 dark:from-gray-900 dark:to-blue-900/20">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: getDuration(0.6) }}
          className="text-center max-w-4xl mx-auto mb-16"
          style={getWillChangeStyle(['opacity', 'transform'])}
        >
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 dark:text-white mb-6">
            {t('bunker.title')}
          </h2>
          <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
            {t('bunker.description')}
          </p>
        </motion.div>

        {/* Features Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
          {featureKeys.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: getDuration(0.6), delay: getDelay(index * 0.05) }}
                className="group bg-white dark:bg-gray-800 rounded-xl p-6 hover:bg-gradient-to-br hover:from-blue-50 hover:to-purple-50 dark:hover:from-blue-900/20 dark:hover:to-purple-900/20 transition-all duration-300 border border-transparent hover:border-blue-200 dark:hover:border-blue-800 hover:shadow-xl"
                style={getWillChangeStyle(['opacity', 'transform'])}
              >
                {/* Icon */}
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-lg bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400 mb-4 group-hover:scale-110 transition-transform">
                  <Icon className="w-6 h-6" />
                </div>

                {/* Title */}
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  {t(`bunker.${feature.key}.title`)}
                </h3>

                {/* Description */}
                <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">
                  {t(`bunker.${feature.key}.description`)}
                </p>
              </motion.div>
            );
          })}
        </div>

        {/* CTA Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: getDuration(0.6), delay: getDelay(0.5) }}
          className="text-center"
          style={getWillChangeStyle(['opacity', 'transform'])}
        >
          <Link
            to={`/${currentLang}/bunker`}
            className="px-8 py-4 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-semibold text-lg shadow-lg hover:shadow-xl transition-all duration-300 inline-flex items-center gap-2"
          >
            {t('bunker.learnMoreButton')}
            <ArrowRight className="w-5 h-5" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
}
